import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Check, Download, Lock, X } from 'lucide-react'
import { Francisco } from '@/components/francisco/Francisco'
import { iconOnly, panelBox, primaryButton, quietButton, secondaryButton, small, Unavailable } from '@/components/shell/StatusScreens'
import { ExampleBadge } from '@/components/ui/ExampleBadge'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { adminMeQuery, meCan, useAdminExport } from './api'
import { EXPORT_REASONS, EXPORT_REASON_LABELS, type AdminAreaSlug, type ExportInput } from './contract'

/* Spec COG-009 — building blocks every Admin screen uses, in the panel's own look (BALANCE.md):
   page header with "Admin / group" trail, cards, tone chips, KPI tiles, segmented filters, tabs, the export-with-reason
   panel and the "no access" / loading / unavailable states. */

export type Tone = 'ok' | 'warn' | 'err' | 'info' | 'neutral' | 'green'

const TONES: Record<Tone, { bg: string; ink: string; dot: string }> = {
    ok: { bg: 'var(--ok-bg)', ink: 'var(--ok-ink)', dot: 'var(--ok)' },
    warn: { bg: 'var(--yellow-bg)', ink: 'var(--yellow-ink)', dot: 'var(--yellow)' },
    err: { bg: 'var(--red-bg)', ink: 'var(--red-ink)', dot: 'var(--red)' },
    info: { bg: 'var(--info-bg)', ink: 'var(--info)', dot: 'var(--info)' },
    neutral: { bg: 'var(--raised)', ink: 'var(--muted)', dot: 'var(--line-2)' },
    green: { bg: 'var(--tint)', ink: 'var(--green)', dot: 'var(--green)' },
}
export const toneColors = (tone: Tone) => TONES[tone]

export function Chip({ tone = 'neutral', dot = false, children, className }: { tone?: Tone; dot?: boolean; children: ReactNode; className?: string }) {
    const t = TONES[tone]
    return (
        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[12px] font-medium whitespace-nowrap', className)} style={{ background: t.bg, color: t.ink }}>
            {dot && <span aria-hidden="true" className="size-1.5 flex-none rounded-full" style={{ background: t.dot }} />}
            {children}
        </span>
    )
}

export function AdminPageHeader({ group, title, subtitle, example, children }: { group: string; title: string; subtitle?: string; example?: boolean; children?: ReactNode }) {
    return (
        <section aria-labelledby="admin-title" className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5">
            <div className="flex min-w-0 flex-col gap-1">
                <nav aria-label="Você está em" className="flex items-center gap-1.5 text-[12px] font-medium text-muted">
                    <span>Staff</span>
                    <span aria-hidden="true">/</span>
                    <span>{group}</span>
                </nav>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h1 id="admin-title" className="m-0 text-[24px] leading-[1.15] font-bold tracking-[-0.01em]">{title}</h1>
                    {subtitle && <span className="text-[14px] text-muted">· {subtitle}</span>}
                    <ExampleBadge show={!!example} />
                </div>
            </div>
            {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
        </section>
    )
}

export function Card({ title, aside, children, className, id }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string; id?: string }) {
    const headingId = id ? `${id}-title` : undefined
    return (
        <section aria-labelledby={title ? headingId : undefined} className={cn(panelBox, 'flex flex-col gap-2.5', className)}>
            {(title || aside) && (
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    {title && <h2 id={headingId} className="m-0 text-[18px] leading-[1.25] font-semibold">{title}</h2>}
                    {aside && <div className="text-[12.5px] text-muted">{aside}</div>}
                </div>
            )}
            {children}
        </section>
    )
}

export function Kpi({ label, value, sub, tone, onClick, pressed }: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone; onClick?: () => void; pressed?: boolean }) {
    const body = (
        <>
            <span className="block text-[12px] text-muted">{label}</span>
            <span className="tabular mt-1 block text-[22px] leading-[1.1] font-semibold tracking-[-0.01em]" style={tone ? { color: TONES[tone].ink } : undefined}>{value}</span>
            {sub && <span className="mt-0.5 block text-[12px] text-muted">{sub}</span>}
        </>
    )
    const box = 'min-w-0 rounded-[14px] border border-line bg-panel px-4 py-3 text-left'
    if (!onClick) return <div className={box}>{body}</div>
    return (
        <button type="button" onClick={onClick} aria-pressed={pressed} className={cn(box, 'cursor-pointer font-[inherit] text-fg hover:bg-[var(--hover)]', pressed && 'ring-1 ring-[var(--green)]')}>
            {body}
        </button>
    )
}

export function KpiRow({ children }: { children: ReactNode }) {
    return <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>{children}</div>
}

export type Option<T extends string> = { id: T; label: string; count?: number }

/** Pill group (aria-pressed), like the period picker of Consumo. */
export function Segmented<T extends string>({ label, options, value, onChange }: { label: string; options: readonly Option<T>[]; value: T; onChange: (id: T) => void }) {
    return (
        <div role="group" aria-label={label} className="inline-flex flex-wrap gap-0.5 rounded-xl border border-line bg-bg p-[3px]">
            {options.map(o => {
                const on = o.id === value
                return (
                    <button
                        key={o.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChange(o.id)}
                        className="min-h-8 cursor-pointer rounded-[9px] border-0 px-3 text-[12.5px] font-medium whitespace-nowrap"
                        style={on ? { background: 'var(--panel)', color: 'var(--text)', boxShadow: '0 1px 2px var(--shadow)' } : { background: 'transparent', color: 'var(--muted)' }}
                    >
                        {o.label}
                        {o.count !== undefined && <span className="tabular ml-1 font-normal text-muted">{o.count}</span>}
                    </button>
                )
            })}
        </div>
    )
}

/** Underlined tabs (role=tablist) for switching views inside one screen. They wrap to a second line instead of scrolling. */
export function Tabs<T extends string>({ label, options, value, onChange }: { label: string; options: readonly Option<T>[]; value: T; onChange: (id: T) => void }) {
    return (
        <div role="tablist" aria-label={label} className="flex flex-wrap gap-x-0.5 border-b border-line">
            {options.map(o => {
                const on = o.id === value
                return (
                    <button
                        key={o.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => onChange(o.id)}
                        className="-mb-px cursor-pointer border-0 border-b-2 bg-transparent px-3.5 py-2.5 text-[13.5px] font-medium whitespace-nowrap"
                        style={{ borderBottomColor: on ? 'var(--green)' : 'transparent', color: on ? 'var(--text)' : 'var(--muted)' }}
                    >
                        {o.label}
                        {o.count !== undefined && <span className="tabular ml-1 text-[12px] font-normal text-muted">{o.count}</span>}
                    </button>
                )
            })}
        </div>
    )
}

/** Horizontal bar 0..100 (decor; pair it with the number in text). */
export function Bar({ value, tone = 'green', height = 6 }: { value: number; tone?: Tone; height?: number }) {
    return (
        <span aria-hidden="true" className="block w-full overflow-hidden rounded-full bg-[var(--raised)]" style={{ height }}>
            <span className="block h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: TONES[tone].dot }} />
        </span>
    )
}

export function NoAccess({ title, text }: { title: string; text: string }) {
    return (
        <section role="note" className={cn(panelBox, 'flex flex-wrap items-center gap-4 py-6')}>
            <Francisco color="g" mood="thinking" size={48} follow={false} />
            <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1">
                <span className="flex items-center gap-1.5 text-[16px] font-semibold"><Lock size={16} aria-hidden="true" />{title}</span>
                <span className="text-[14px] text-muted">{text}</span>
            </div>
        </section>
    )
}

/**
 * Loading / 403 / unavailable around an area query; renders children with the data otherwise.
 * 403 shows NoAccess with the area's own message.
 */
export function AreaState<T>({ query, noAccess, children }: { query: UseQueryResult<T>; noAccess: { title: string; text: string }; children: (data: T) => ReactNode }) {
    if (query.error instanceof ApiError && query.error.code === 'forbidden') return <NoAccess {...noAccess} />
    if (query.isPending) return <div className={cn(panelBox, 'text-[14px] text-muted')} role="status">Carregando…</div>
    if (query.isError) return <div className={panelBox}><Unavailable error={query.error} onRetry={() => void query.refetch()} /></div>
    return <>{children(query.data)}</>
}

/**
 * "Exportar" button + popover anchored under it (overlays the page, never pushes the layout).
 * Reason required; personal data only with permission; addresses never. Closes on Esc, outside click or Fechar;
 * after a successful export the popover shows the confirmation instead of the form.
 */
export function ExportControl({ slug, scope }: { slug: AdminAreaSlug; scope?: string }) {
    const me = useQuery(adminMeQuery).data
    const allowPersonal = meCan(me, 'export.dados_pessoais')
    const exporter = useAdminExport(slug)
    const [open, setOpen] = useState(false)
    const [format, setFormat] = useState<ExportInput['format']>('xlsx')
    const [reason, setReason] = useState<ExportInput['reason'] | ''>('')
    const [note, setNote] = useState('')
    const [personal, setPersonal] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const firstFieldRef = useRef<HTMLSelectElement>(null)
    const panelId = useId()
    const noteMissing = reason === 'outro' && note.trim().length < 3
    const canSend = reason !== '' && !noteMissing && !exporter.isPending

    // The mutation object changes every render; keep reset in a ref so close() (and the listeners) stay stable
    const resetRef = useRef(exporter.reset)
    resetRef.current = exporter.reset

    const close = useCallback((returnFocus = true) => {
        setOpen(false)
        setReason('')
        setNote('')
        setPersonal(false)
        resetRef.current()
        if (returnFocus) buttonRef.current?.focus()
    }, [])

    // Focus the first field only when the popover opens
    useEffect(() => {
        if (open) firstFieldRef.current?.focus()
    }, [open])

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
        const onDown = (e: PointerEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false) }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open, close])

    if (!meCan(me, 'export.basico')) return null
    const fieldLabel = 'flex flex-col gap-1 text-[12.5px] font-medium text-muted'
    const input = 'min-h-9 rounded-[10px] border border-line-2 bg-panel px-2.5 text-[14px] font-normal text-fg'
    return (
        <div ref={rootRef} className="relative">
            <button
                ref={buttonRef}
                type="button"
                className={secondaryButton}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => (open ? close() : setOpen(true))}
            >
                <Download aria-hidden="true" />Exportar
            </button>
            {open && (
                <div
                    id={panelId}
                    role="dialog"
                    aria-label={`Exportar${scope ? ` · ${scope}` : ''}`}
                    className="absolute top-full right-0 z-30 mt-2 flex w-[min(380px,calc(100vw-32px))] flex-col rounded-[14px] border border-line bg-panel text-left"
                    style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
                >
                    <div className="flex items-start justify-between gap-3 border-b border-line px-4 pt-3.5 pb-3">
                        <div className="flex min-w-0 flex-col">
                            <span className="text-[15px] font-semibold">Exportar</span>
                            {scope && <span className="truncate text-[12.5px] text-muted">{scope}</span>}
                        </div>
                        <button type="button" className={cn(quietButton, small, iconOnly)} aria-label="Fechar" onClick={() => close()}>
                            <X aria-hidden="true" />
                        </button>
                    </div>

                    {exporter.isSuccess ? (
                        <div role="status" className="flex flex-col gap-3 px-4 py-4">
                            <div className="flex items-start gap-2.5">
                                <span className="grid size-8 flex-none place-items-center rounded-full bg-[var(--ok-bg)] text-[var(--ok-ink)]"><Check size={16} aria-hidden="true" /></span>
                                <div className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-[14px] font-medium break-all">{exporter.data.fileName}</span>
                                    <span className="text-[13px] text-muted">
                                        {exporter.data.rows} linhas · {exporter.data.includePersonal ? 'com' : 'sem'} dados pessoais · endereços nunca saem
                                    </span>
                                </div>
                            </div>
                            <span className="rounded-[10px] bg-[var(--raised)] px-3 py-2 text-[12.5px] text-muted">Registrado no histórico de Equipe, com o motivo.</span>
                            <div className="flex justify-end gap-2">
                                <button type="button" className={cn(quietButton, small)} onClick={() => exporter.reset()}>Exportar outro</button>
                                <button type="button" className={cn(primaryButton, small)} onClick={() => close()}>Concluir</button>
                            </div>
                        </div>
                    ) : (
                        <form
                            className="flex flex-col"
                            onSubmit={e => {
                                e.preventDefault()
                                if (!canSend || !reason) return
                                exporter.mutate({ format, reason, note: note.trim() || undefined, includePersonal: personal, scope })
                            }}
                        >
                            <div className="flex flex-col gap-3 px-4 py-3.5">
                                <label className={fieldLabel}>
                                    Motivo da exportação
                                    <select ref={firstFieldRef} required value={reason} onChange={e => setReason(e.target.value as ExportInput['reason'])} className={input}>
                                        <option value="" disabled>Escolha o motivo</option>
                                        {EXPORT_REASONS.map(r => <option key={r} value={r}>{EXPORT_REASON_LABELS[r]}</option>)}
                                    </select>
                                </label>
                                {reason === 'outro' && (
                                    <label className={fieldLabel}>
                                        Conte o motivo
                                        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={300} className={cn(input, 'py-2')} />
                                    </label>
                                )}
                                <div className={fieldLabel}>
                                    Formato
                                    <Segmented label="Formato" value={format} onChange={setFormat} options={[{ id: 'xlsx', label: 'Excel' }, { id: 'csv', label: 'CSV' }]} />
                                </div>
                                <label className={cn('flex items-start gap-2 text-[13px] leading-[1.4]', !allowPersonal && 'text-muted')}>
                                    <input type="checkbox" checked={personal} disabled={!allowPersonal} onChange={e => setPersonal(e.target.checked)} className="mt-0.5 accent-[var(--green)]" />
                                    <span>
                                        Incluir dados pessoais
                                        <span className="block text-[12px] text-muted">
                                            {allowPersonal ? 'Nomes e contatos completos. Endereços nunca saem.' : 'O seu papel não tem a permissão Exportar dados pessoais.'}
                                        </span>
                                    </span>
                                </label>
                                {exporter.isError && <span role="alert" className="text-[13px] text-[var(--red-ink)]">Não deu para exportar. Confira o motivo e a permissão.</span>}
                            </div>
                            <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
                                <button type="button" className={cn(quietButton, small)} onClick={() => close()}>Cancelar</button>
                                <button type="submit" className={cn(primaryButton, small)} disabled={!canSend}>
                                    <Download aria-hidden="true" />{exporter.isPending ? 'Exportando…' : 'Exportar'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </div>
    )
}
